import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiPlus, FiRotateCcw, FiTrash2, FiX } from 'react-icons/fi';
import aiAutomationApi from '../utils/aiAutomationApi';

const SECTION_NAMES = {
  screening: 'Screening', functional: 'Functional', behavioral: 'Behavioral',
  managerial: 'Managerial', recruiter_question: 'Recruiter questions',
  practical_task: 'Practical task', other: 'Other',
};
const EDITABLE_SECTIONS = ['screening', 'functional', 'behavioral', 'managerial'];
const isProtected = (row) => ['recruiter_question', 'practical_task'].includes(row.interview_mode)
  || /^(recruiter question|recruiter-provided question|practical task)/i.test(row.label || '')
  || /^(recruiter-provided question|saved task rubric)/i.test(row.assessment_method || '');

export const balanceScorecard = (rows, editedIndex, requestedWeight) => {
  if (!rows.length) return rows;
  const peers = rows.map((_, index) => index).filter((index) => index !== editedIndex);
  const maxWeight = 100 - peers.length;
  if (!Number.isInteger(requestedWeight) || requestedWeight < 1 || requestedWeight > maxWeight) {
    return null;
  }
  if (!peers.length) return rows.map((row) => ({ ...row, weight: 100 }));
  const available = 100 - requestedWeight - peers.length;
  const basis = peers.map((index) => Math.max(0, Number(rows[index].weight || 0) - 1));
  const basisTotal = basis.reduce((sum, value) => sum + value, 0);
  const exact = peers.map((_, position) => available * (basisTotal ? basis[position] / basisTotal : 1 / peers.length));
  const extras = exact.map(Math.floor);
  let left = available - extras.reduce((sum, value) => sum + value, 0);
  const order = peers.map((_, position) => position).sort((a, b) =>
    (exact[b] - extras[b]) - (exact[a] - extras[a]) || a - b);
  for (const position of order) {
    if (left-- <= 0) break;
    extras[position] += 1;
  }
  return rows.map((row, index) => {
    if (index === editedIndex) return { ...row, weight: requestedWeight };
    const position = peers.indexOf(index);
    return { ...row, weight: 1 + extras[position] };
  });
};

const InterviewScorecardModal = ({ sessionId, initialSetupId = null, view = 'draft', onClose, onSaved }) => {
  const dialogRef = useRef(null);
  const returnFocusRef = useRef(document.activeElement);
  const [data, setData] = useState(null);
  const [selectedSetupId, setSelectedSetupId] = useState(initialSetupId == null ? '' : String(initialSetupId));
  const [rows, setRows] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [coverage, setCoverage] = useState(80);
  const [autoBalance, setAutoBalance] = useState(true);
  const [newLabel, setNewLabel] = useState('');
  const [newSection, setNewSection] = useState('functional');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  busyRef.current = busy;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [resetPrompt, setResetPrompt] = useState(false);
  const [resetToStarting, setResetToStarting] = useState(false);
  const [lastEditedKey, setLastEditedKey] = useState('');

  useEffect(() => {
    let live = true;
    setData(null);
    setError('');
    const query = view === 'draft' ? '?draft_only=true' : selectedSetupId ? `?setup_id=${encodeURIComponent(selectedSetupId)}` : '';
    aiAutomationApi.get(`/chat/setup-scorecard/${encodeURIComponent(sessionId)}${query}`)
      .then((value) => {
        if (!live) return;
        setData(value);
        setRows(value.rows || []);
        setRemoved(value.removed_rows || []);
        setCoverage(value.minimum_assessed_weight_percent || 80);
        setNewSection(EDITABLE_SECTIONS.find((key) => value.selected_sections?.includes(key)) || 'functional');
        setResetPrompt(false);
        setResetToStarting(false);
        setLastEditedKey('');
      })
    .catch((err) => live && setError(view === 'draft' && err.status === 409
      ? 'The current setup draft could not be loaded. Refresh the chat and try again. Saved setups remain available in the right panel.'
      : err.message || 'Could not load the scorecard'));
    return () => { live = false; };
  }, [sessionId, selectedSetupId, view]);

  useEffect(() => {
    const returnFocus = returnFocusRef.current;
    dialogRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !busyRef.current) onCloseRef.current();
      if (event.key !== 'Tab') return;
      const controls = [...(dialogRef.current?.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled)') || [])];
      if (!controls.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => { document.removeEventListener('keydown', onKeyDown); returnFocus?.focus?.(); };
  }, []);

  const total = rows.reduce((sum, row) => sum + Number(row.weight || 0), 0);
  const canEdit = Boolean(data && view === 'draft' && !data.read_only && data.setup_step);
  const groups = useMemo(() => Object.keys(SECTION_NAMES).map((key) => ({
    key, label: SECTION_NAMES[key], rows: rows.filter((row) => (row.interview_mode || 'other') === key),
    total: rows.filter((row) => (row.interview_mode || 'other') === key)
      .reduce((sum, row) => sum + Number(row.weight || 0), 0),
  })).filter((group) => group.rows.length), [rows]);

  const editWeight = (key, raw) => {
    const value = Number(raw);
    const index = rows.findIndex((row) => row.key === key);
    if (index < 0 || !Number.isInteger(value)) return;
    if (autoBalance) {
      const balanced = balanceScorecard(rows, index, value);
      if (!balanced) {
        setError(`Keep at least 1% for each of the other ${rows.length - 1} topics.`);
        return;
      }
      setRows(balanced);
    } else {
      setRows(rows.map((row, position) => position === index ? { ...row, weight: value } : row));
    }
    setLastEditedKey(key);
    setResetToStarting(false);
    setError('');
  };

  const removeTopic = (key) => {
    const row = rows.find((item) => item.key === key);
    if (!row || isProtected(row) || rows.length < 2) return;
    const next = rows.filter((item) => item.key !== key);
    setRemoved([...removed.filter((item) => item.key !== key), row]);
    setRows(autoBalance ? balanceScorecard(next, 0, Math.min(next[0].weight, 100 - next.length + 1)) : next);
    setResetToStarting(false);
    setError('');
  };

  const restoreTopic = (key) => {
    const row = removed.find((item) => item.key === key);
    if (!row || rows.length >= 24) return;
    const next = [...rows, { ...row, weight: Math.max(1, Number(row.weight || 1)) }];
    const balanced = autoBalance ? balanceScorecard(next, next.length - 1, Math.min(next.at(-1).weight, 100 - rows.length)) : next;
    setRows(balanced || next);
    setRemoved(removed.filter((item) => item.key !== key));
    setResetToStarting(false);
    setError('');
  };

  const addTopic = () => {
    const label = newLabel.trim().replace(/\s+/g, ' ');
    if (!label || label.length > 160 || rows.length >= 24 ||
        [...rows, ...removed].some((row) => row.label.toLowerCase() === label.toLowerCase())) {
      setError('Enter a unique topic name (up to 160 characters); the limit is 24 topics.');
      return;
    }
    const section = data.selected_sections?.includes(newSection) ? newSection : data.selected_sections?.[0];
    if (!EDITABLE_SECTIONS.includes(section)) {
      setError('Choose a selected interview section.');
      return;
    }
    const next = [...rows, { key: `new:${Date.now()}_${Math.random().toString(36).slice(2)}`, label, interview_mode: section, weight: 1 }];
    setRows(autoBalance ? balanceScorecard(next, next.length - 1, 1) || next : next);
    setResetToStarting(false);
    setNewLabel('');
    setError('');
  };

  const save = async () => {
    if (!rows.length || rows.some((row) => !Number.isInteger(Number(row.weight)) || Number(row.weight) < 1)
        || total !== 100 || !Number.isInteger(Number(coverage)) || Number(coverage) < 1 || Number(coverage) > 100) {
      setError('Use positive whole-number topic weights totaling 100%, and a coverage target from 1 to 100%.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const saved = await aiAutomationApi.put(`/chat/setup-scorecard/${encodeURIComponent(sessionId)}`, {
        revision: data.revision, action: resetToStarting ? 'reset' : 'save',
        rows: rows.map(({ key, label, interview_mode, weight }) => ({ key, label, interview_mode, weight: Number(weight) })),
        minimum_assessed_weight_percent: Number(coverage),
      }, { timeout: 90000 });
      onSaved(saved);
    } catch (err) {
      setError(err.status === 409 ? 'The setup changed. Close and reopen the editor to load the latest scorecard.' :
        err.message || 'Could not save the scorecard. Your edits are still here.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-3" role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && !busy && onClose()}>
      <div ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="scorecard-editor-title"
        className="flex w-full max-w-3xl max-h-[92vh] flex-col overflow-hidden rounded-lg bg-white text-gray-900 shadow-xl dark:bg-gray-900 dark:text-gray-100">
        <header className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div><h2 id="scorecard-editor-title" className="text-lg font-semibold">Assessment topics</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{view === 'saved' ? 'Confirmed setup · read only' : 'Overall weights for this interview setup'}</p></div>
          <button type="button" title="Close editor" aria-label="Close editor" disabled={busy} onClick={onClose}
            className="rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-800"><FiX /></button>
        </header>
        <div className={`overflow-y-auto px-5 py-4 space-y-5 ${busy ? 'pointer-events-none opacity-60' : ''}`}>
          {!data ? !error && <p className="text-sm">Loading scorecard...</p> : <>
            {view === 'saved' && data.setups?.filter((setup) => setup.read_only).length > 1 && <label className="flex flex-col gap-1 text-sm font-medium">
              Interview setup
              <select value={data.selected_setup_id == null ? '' : String(data.selected_setup_id)}
                onChange={(event) => setSelectedSetupId(event.target.value)}
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-800">
                {data.setups.filter((setup) => setup.read_only).map((setup) => <option key={setup.setup_id} value={setup.setup_id}>
                  {setup.name}
                </option>)}
              </select>
            </label>}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 pb-3 dark:border-gray-700">
              {canEdit && <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={autoBalance} onChange={(event) => {
                  const on = event.target.checked;
                  setAutoBalance(on);
                  if (on && rows.length && total !== 100) {
                    const index = Math.max(0, rows.findIndex((row) => row.key === lastEditedKey));
                    setRows(balanceScorecard(rows, index, Math.min(Math.max(1, rows[index].weight), 100 - rows.length + 1)) || rows);
                  }
                }} /> Auto-balance weights
              </label>}
              <strong className={total === 100 ? 'text-emerald-700' : 'text-red-600'}>{total}% / 100%</strong>
            </div>
            {!rows.length && <p className="text-sm text-gray-500">No scorecard is stored for this setup.</p>}
            {groups.map((group) => <section key={group.key} aria-label={group.label}>
              <div className="mb-2 flex justify-between text-sm font-semibold"><span>{group.label}</span><span>{group.total}% overall</span></div>
              <div className="divide-y divide-gray-100 border-y border-gray-100 dark:divide-gray-800 dark:border-gray-800">
                {group.rows.map((row) => <div key={row.key} className="flex items-center gap-3 py-2">
                  <span className="min-w-0 flex-1 text-sm break-words">{row.display_label || row.label}</span>
                  <label className="flex items-center gap-1 text-sm"><span className="sr-only">Weight for {row.label}</span>
                    <input type="number" min="1" max="100" step="1" value={row.weight} disabled={!canEdit}
                      onChange={(event) => editWeight(row.key, event.target.value)}
                      className="w-16 rounded border border-gray-300 bg-white px-2 py-1 text-right dark:border-gray-600 dark:bg-gray-800" />%
                  </label>
                  {canEdit && <button type="button" title={isProtected(row) ? 'This topic is required by a question or task' : `Remove ${row.label}`}
                    aria-label={`Remove ${row.label}`} disabled={isProtected(row)} onClick={() => removeTopic(row.key)}
                    className="rounded p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-gray-800"><FiTrash2 /></button>}
                </div>)}
              </div>
            </section>)}
            {canEdit && removed.length > 0 && <section><h3 className="mb-2 text-sm font-semibold">Removed topics</h3>
              <div className="space-y-1">{removed.map((row) => <div key={row.key} className="flex items-center justify-between gap-3 text-sm text-gray-500">
                <span className="min-w-0 break-words">{row.display_label || row.label}</span><button type="button" onClick={() => restoreTopic(row.key)}
                  className="shrink-0 text-indigo-600 hover:underline dark:text-indigo-400">Restore</button>
              </div>)}</div></section>}
            {canEdit && <section className="border-t border-gray-200 pt-4 dark:border-gray-700">
              <h3 className="mb-2 text-sm font-semibold">Add topic</h3>
              <div className="flex flex-wrap gap-2"><input value={newLabel} onChange={(event) => setNewLabel(event.target.value)}
                placeholder="Topic name" aria-label="New topic name" className="min-w-48 flex-1 rounded border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800" />
                <select value={newSection} onChange={(event) => setNewSection(event.target.value)} aria-label="New topic section"
                  className="rounded border border-gray-300 bg-white px-2 py-2 text-sm dark:border-gray-600 dark:bg-gray-800">
                  {EDITABLE_SECTIONS.filter((key) => data.selected_sections?.includes(key)).map((key) =>
                    <option key={key} value={key}>{SECTION_NAMES[key]}</option>)}</select>
                <button type="button" onClick={addTopic} className="flex items-center gap-1 rounded border border-gray-300 px-3 py-2 text-sm dark:border-gray-600"><FiPlus /> Add</button>
              </div><p className="mt-1 text-xs text-gray-500">Wiza will prepare the evidence target when you save.</p>
            </section>}
            <label className="flex items-center gap-2 text-sm">Minimum assessed weight for a complete result
              <input type="number" min="1" max="100" step="1" value={coverage} disabled={!canEdit}
                onChange={(event) => { setCoverage(event.target.value); setResetToStarting(false); }}
                className="w-16 rounded border border-gray-300 bg-white px-2 py-1 text-right dark:border-gray-600 dark:bg-gray-800" />%
            </label>
          </>}
          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        </div>
        <footer className="flex items-center justify-between gap-3 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>{canEdit && (resetPrompt ? <span className="flex items-center gap-2 text-xs">Restore AI starting scorecard?
            <button type="button" disabled={busy} onClick={() => { setRows(data.starting_rows || []); setRemoved([]); setCoverage(80); setResetToStarting(true); setResetPrompt(false); setError(''); }}
              className="font-semibold text-red-600">Restore</button>
            <button type="button" disabled={busy} onClick={() => setResetPrompt(false)}>Cancel</button></span> :
            <button type="button" disabled={!data || busy} onClick={() => setResetPrompt(true)}
              className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300"><FiRotateCcw /> Reset</button>)}</div>
          <div className="flex gap-2"><button type="button" disabled={busy} onClick={onClose} className="rounded border border-gray-300 px-4 py-2 text-sm dark:border-gray-600">{canEdit ? 'Cancel' : 'Close'}</button>
            {canEdit && <button type="button" disabled={busy || total !== 100} onClick={save}
              className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">{busy ? 'Saving...' : 'Save'}</button>}</div>
        </footer>
      </div>
    </div>
  );
};

export default InterviewScorecardModal;
